import * as Sentry from '@sentry/react';
import { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { gymApi } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { Client, CheckIn } from '@/types';
import { Html5Qrcode, Html5QrcodeSupportedFormats } from 'html5-qrcode';
import {
  QrCode,
  Camera,
  CheckCircle2,
  XCircle,
  Clock,
  Wifi,
  AlertCircle,
  ShieldAlert,
  Building2,
  ExternalLink,
  Search,
  Loader2,
  User as UserIcon
} from 'lucide-react';
import { NoGymSelected } from '@/components/NoGymSelected';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

export default function ScannerPage() {
  const navigate = useNavigate();
  const { gym, hasRole } = useAuth();
  const isSuperAdmin = hasRole(['super_admin']);
  const [isScanning, setIsScanning] = useState(false);
  const [manualCode, setManualCode] = useState('');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [lastScan, setLastScan] = useState<{
    success: boolean;
    message: string;
    clientName?: string;
    timestamp: string;
    sessionsRemaining?: number | null;
  } | null>(null);
  const [recentScans, setRecentScans] = useState<CheckIn[]>([]);
  const [cooldown, setCooldown] = useState(false);
  const [clientSearch, setClientSearch] = useState('');
  const [clientResults, setClientResults] = useState<Client[]>([]);
  const [isSearchingClients, setIsSearchingClients] = useState(false);
  const [pendingClient, setPendingClient] = useState<Client | null>(null);
  const [isRegistering, setIsRegistering] = useState(false);
  const { toast } = useToast();
  
  useEffect(() => {
    Sentry.setTag("area", "scanner");
  }, []);

  // Debounced client search for manual check-in
  useEffect(() => {
    if (clientSearch.trim().length < 2) {
      setClientResults([]);
      return;
    }
    const timer = setTimeout(async () => {
      setIsSearchingClients(true);
      try {
        const res = await gymApi.findClientByIdentifier(gym?.gym_id || '', clientSearch.trim());
        setClientResults(res.success ? (res.clients || []) : []);
      } catch {
        setClientResults([]);
      } finally {
        setIsSearchingClients(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [clientSearch, gym?.gym_id]);

  // Ref to hold the scanner instance
  const scannerRef = useRef<Html5Qrcode | null>(null);
  // Ref to track processing state
  const isProcessingRef = useRef(false);
  // Ref for throttling scans (software debounce)
  const lastScannedRef = useRef<number>(0);

  // Check if we are in a secure context or localhost
  const isSecure = window.isSecureContext;

  // Initial fetch of recent check-ins
  useEffect(() => {
    const fetchRecent = async () => {
      try {
        const res = await gymApi.listCheckIns(null, gym?.gym_id || undefined);
        if (res.success && Array.isArray(res.check_ins)) {
          setRecentScans(res.check_ins.slice(0, 10));
        }
      } catch (error) {
        console.error("Error fetching check-ins:", error);
      }
    };
    fetchRecent();
  }, [gym?.gym_id]);

  // Main Effect for Scanner Logic
  useEffect(() => {
    let mounted = true;
    const readerId = "reader";

    const startCamera = async () => {
      if (isProcessingRef.current) return;
      isProcessingRef.current = true;
      setErrorMsg(null);

      try {
        // 1. Get Cameras
        let devices;
        try {
          devices = await Html5Qrcode.getCameras();
        } catch (err) {
          console.warn("GetCameras error:", err);
          Sentry.captureException(err, {
            tags: { component: 'ScannerPage', step: 'getCameras' },
            extra: { isSecureContext: window.isSecureContext, userAgent: navigator.userAgent },
          });
          throw err;
        }

        if (!mounted) return;

        if (devices && devices.length) {
          // 2. Clear existing instance if any
          if (scannerRef.current) {
            try {
              if (scannerRef.current.isScanning) {
                await scannerRef.current.stop();
              }
              scannerRef.current.clear();
            } catch (e) { console.warn("Cleanup warning:", e); }
            scannerRef.current = null;
          }

          // 3. Create new instance
          const html5QrCode = new Html5Qrcode(readerId);
          scannerRef.current = html5QrCode;

          const config = {
            fps: 10,
            qrbox: (viewfinderWidth: number, viewfinderHeight: number) => {
              const minEdge = Math.min(viewfinderWidth, viewfinderHeight);
              const fontSize = Math.floor(minEdge * 0.6);
              return { width: fontSize, height: fontSize };
            },
            aspectRatio: 1.0,
            formatsToSupport: [Html5QrcodeSupportedFormats.QR_CODE],
            showTorchButtonIfSupported: true,
          };

          const qrCodeSuccessCallback = (decodedText: string) => {
            // SOFTWARE THROTTLE:
            // Instead of stopping/pausing the camera (which causes state errors),
            // we simply ignore scans that happen too close together.
            const now = Date.now();
            if (now - lastScannedRef.current < 5000) {
              return; // Ignore this scan (5 second cooldown)
            }
            lastScannedRef.current = now;
            setCooldown(true);
            handleScan(decodedText);
            
            // Re-enable scanning after 5 seconds
            setTimeout(() => {
              setCooldown(false);
            }, 5000);
          };

          // 4. Try Environment Camera first
          try {
            await html5QrCode.start(
              { facingMode: "environment" },
              config,
              qrCodeSuccessCallback,
              (errorMessage) => { /* ignore frame errors */ }
            );
          } catch (err) {
            console.warn('⚠️ [FALLBACK] No environment camera, trying first available...', err);
            if (!mounted) return;

            // 5. Fallback to first available camera
            await html5QrCode.start(
              devices[0].id,
              config,
              qrCodeSuccessCallback,
              (errorMessage) => { /* ignore frame errors */ }
            );
          }
        } else {
          setErrorMsg("No se encontraron cámaras. Verifica que tu dispositivo tenga cámara.");
          setIsScanning(false);
        }
      } catch (err: any) {
        console.error("Critical Scanner Error:", err);
        Sentry.captureException(err, {
          tags: { component: 'ScannerPage', step: 'startCamera' },
          extra: {
            isSecureContext: window.isSecureContext,
            userAgent: navigator.userAgent,
            errorName: err?.name,
          },
        });
        if (mounted) {
          setIsScanning(false);

          let msg = 'Error al acceder a la cámara.';
          if (err.name === 'NotAllowedError' || err.message?.includes('permission')) {
            msg = 'Permiso denegado. Permite el acceso a la cámara en tu navegador.';
          } else if (!isSecure && err.message?.includes('supported')) {
            msg = 'Contexto no seguro. Los navegadores requieren HTTPS para usar la cámara.';
          } else if (err.name === 'NotFoundError') {
            msg = 'No se encontró ninguna cámara.';
          } else if (err.name === 'NotReadableError') {
            msg = 'La cámara está en uso por otra aplicación.';
          }
          setErrorMsg(msg);
        }
      } finally {
        isProcessingRef.current = false;
      }
    };

    const stopCamera = async () => {
      // Capture local ref to avoid race conditions with cleanup
      const scanner = scannerRef.current;
      if (!scanner) return;

      // Detach immediately
      scannerRef.current = null;

      try {
        if (scanner.isScanning) {
          await scanner.stop();
        }
        scanner.clear();
      } catch (err) {
        console.warn("Stop error ignored:", err);
      }
    };

    if (isScanning) {
      // Small delay to ensure DOM is ready
      setTimeout(() => {
        if (mounted && document.getElementById(readerId)) {
          startCamera();
        }
      }, 100);
    } else {
      stopCamera();
    }

    return () => {
      mounted = false;
      stopCamera();
    };
  }, [isScanning]);


  const processCheckInResult = (
    res: { result?: string; message?: string; client_name?: string; sessions_remaining?: number | null },
    clientId: string,
    fallbackName?: string
  ) => {
    const timestamp = new Date().toISOString();
    const allowed = res.result === 'allowed';
    const clientName = res.client_name || fallbackName;

    setLastScan({
      success: allowed,
      message: res.message || (allowed ? 'Acceso permitido' : 'Acceso denegado'),
      clientName,
      timestamp,
      sessionsRemaining: res.sessions_remaining ?? null,
    });

    const newCheckIn: CheckIn = {
      checkin_id: `TEMP_${Date.now()}`,
      client_id: clientId,
      gym_id: gym?.gym_id || '',
      timestamp: timestamp,
      result: res.result as 'allowed' | 'denied',
      client_name: clientName || 'Desconocido',
      method: 'admin_scanner',
      sessions_remaining: res.sessions_remaining ?? null,
    };

    setRecentScans(prev => [newCheckIn, ...prev].slice(0, 10));

    toast({
      title: allowed ? '✓ Acceso permitido' : '✗ Acceso denegado',
      description: clientName || res.message,
      variant: allowed ? 'default' : 'destructive',
    });
  };

  const handleScan = async (qrData: string) => {
    try {
      const res = await gymApi.checkIn(qrData, null, gym?.gym_id || undefined);
      processCheckInResult(res, qrData);
    } catch (error: any) {
      console.error(error);
      
      // Detailed Sentry reporting
      Sentry.captureException(error, {
        tags: {
          area: 'scanner',
          operation: 'check_in',
          gym_id: gym?.gym_id || 'unknown',
        },
        extra: {
          qrData, // Crucial for debugging "SCANNER DOWN"
          gym_name: gym?.name,
        }
      });

      toast({
        title: 'Error',
        description: 'Error al procesar el check-in',
        variant: 'destructive',
      });
    }
  };

  const handleManualCheckIn = async () => {
    if (!pendingClient || !gym || isRegistering) return;
    setIsRegistering(true);
    try {
      const res = await gymApi.selfCheckIn(pendingClient.client_id, gym.gym_id, 'admin_scanner');
      processCheckInResult(res, pendingClient.client_id, pendingClient.name);
      setPendingClient(null);
      setClientSearch('');
      setClientResults([]);
    } catch (error: any) {
      console.error(error);
      Sentry.captureException(error, {
        tags: {
          area: 'scanner',
          operation: 'manual_check_in',
          gym_id: gym?.gym_id || 'unknown',
        },
        extra: {
          client_id: pendingClient.client_id,
          gym_name: gym?.name,
        }
      });
      toast({
        title: 'Error',
        description: 'No se pudo registrar el check-in manual',
        variant: 'destructive',
      });
    } finally {
      setIsRegistering(false);
    }
  };

  const handleManualSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (manualCode.trim()) {
      handleScan(manualCode.trim());
      setManualCode('');
    }
  };

  return (
    <DashboardLayout
      title="Scanner QR"
      subtitle={gym ? `Control de acceso para ${gym.name}` : "Control de acceso global"}
    >
      {isSuperAdmin && !gym && <NoGymSelected />}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="lg:row-span-2">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <QrCode className="h-5 w-5 text-primary" />
              Escanear Código QR
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6 p-4 sm:p-6">

            {!isSecure && (
              <div className="p-4 bg-warning/10 text-orange-700 rounded-lg flex items-start gap-3 border border-orange-200">
                <ShieldAlert className="h-5 w-5 mt-0.5 shrink-0" />
                <div className="text-sm">
                  <p className="font-semibold">Conexión no segura (HTTP)</p>
                  <p>Los navegadores modernos bloquean la cámara en conexiones no seguras. Usa localhost o HTTPS para probar.</p>
                </div>
              </div>
            )}

            {errorMsg && (
              <div className="p-4 bg-destructive/10 text-destructive rounded-lg flex items-start gap-3">
                <AlertCircle className="h-5 w-5 mt-0.5 shrink-0" />
                <div className="text-sm">
                  <p className="font-semibold">Error de cámara</p>
                  <p>{errorMsg}</p>
                </div>
              </div>
            )}

            {/* Scanner Container */}
            <div className={`w-full max-w-[320px] sm:max-w-sm mx-auto overflow-hidden rounded-2xl border-2 ${isScanning ? 'border-primary shadow-lg shadow-primary/20' : 'border-dashed border-border'}`}>
              <div className="relative w-full aspect-square bg-black/5 flex items-center justify-center overflow-hidden">
                {!isScanning && (
                  <div className="absolute inset-0 flex flex-col items-center justify-center p-6 text-center z-10 bg-muted/50">
                    <Camera className="h-16 w-16 text-muted-foreground mb-4" />
                    <p className="text-muted-foreground mb-2">
                      Cámara en espera
                    </p>
                    <p className="text-sm text-muted-foreground">
                      Haz clic en "Iniciar" para escanear
                    </p>
                  </div>
                )}
                {/* The div where html5-qrcode attaches. Must be always present when scanning. */}
                <div id="reader" className="w-full h-full"></div>

                {/* Cooldown Overlay */}
                {cooldown && (
                  <div className={cn(
                    "absolute inset-0 z-20 flex flex-col items-center justify-center backdrop-blur-md transition-all duration-300",
                    lastScan?.success === false ? "bg-destructive/40" : "bg-success/40"
                  )}>
                    {lastScan?.success === false ? (
                      <XCircle className="h-20 w-20 text-white animate-in zoom-in duration-300" />
                    ) : (
                      <CheckCircle2 className="h-20 w-20 text-white animate-in zoom-in duration-300" />
                    )}
                    <div className="mt-4 px-4 py-2 bg-black/60 rounded-full border border-white/20">
                       <p className="text-white font-bold text-xs uppercase tracking-widest">Esperando...</p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="flex gap-3 justify-center">
              <Button
                variant={isScanning ? 'destructive' : 'gradient'}
                size="lg"
                disabled={!gym}
                onClick={() => setIsScanning(!isScanning)}
                className="w-full sm:w-auto"
              >
                {isScanning ? 'Detener Cámara' : 'Iniciar Cámara'}
              </Button>
            </div>

            <div className="border-t border-border pt-6">
              <p className="text-sm font-medium mb-3">Entrada Manual</p>
              <form onSubmit={handleManualSubmit} className="flex gap-2">
                <Input
                  placeholder="Ingresa código QR manualmente..."
                  value={manualCode}
                  onChange={(e) => setManualCode(e.target.value)}
                  className="flex-1"
                />
                <Button type="submit" variant="secondary" disabled={!gym}>
                  Validar
                </Button>
              </form>
            </div>

            <div className="border-t border-border pt-6">
              <p className="text-sm font-medium mb-1">Buscar socio</p>
              <p className="text-xs text-muted-foreground mb-3">
                Registra la entrada cuando el socio olvidó su celular. Busca por nombre o teléfono.
              </p>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Nombre o teléfono del socio..."
                  value={clientSearch}
                  onChange={(e) => setClientSearch(e.target.value)}
                  className="pl-9"
                  disabled={!gym}
                />
                {isSearchingClients && (
                  <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground animate-spin" />
                )}
              </div>

              {clientSearch.trim().length > 0 && clientSearch.trim().length < 3 && (
                <p className="text-xs text-muted-foreground mt-2">Escribe al menos 3 caracteres.</p>
              )}

              {clientSearch.trim().length >= 3 && !isSearchingClients && clientResults.length === 0 && (
                <p className="text-xs text-muted-foreground mt-2">No se encontraron socios.</p>
              )}

              {clientResults.length > 0 && (
                <div className="mt-2 rounded-lg border border-border divide-y divide-border max-h-64 overflow-y-auto">
                  {clientResults.map((client) => (
                    <button
                      key={client.client_id}
                      type="button"
                      onClick={() => setPendingClient(client)}
                      className="w-full text-left px-3 py-2.5 hover:bg-muted transition-colors flex items-center gap-3"
                    >
                      <div className="h-9 w-9 rounded-full bg-primary/10 text-primary flex items-center justify-center shrink-0">
                        <UserIcon className="h-4 w-4" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium truncate">{client.name}</p>
                        <p className="text-xs text-muted-foreground truncate">
                          {client.phone || 'Sin teléfono'}
                        </p>
                      </div>
                      <span className="text-[10px] font-black uppercase tracking-widest text-primary shrink-0">
                        Check-in
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Results Section */}
        <Card className={lastScan ? (lastScan.success ? 'border-success/50' : 'border-destructive/50') : ''}>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              {lastScan?.success ? (
                <CheckCircle2 className="h-5 w-5 text-success" />
              ) : lastScan ? (
                <XCircle className="h-5 w-5 text-destructive" />
              ) : (
                <Wifi className="h-5 w-5 text-muted-foreground" />
              )}
              Último Escaneo
            </CardTitle>
          </CardHeader>
          <CardContent>
            {lastScan ? (
              <div className="space-y-4">
                <div className={`p-6 rounded-xl text-center ${lastScan.success
                  ? 'bg-success/10 text-success'
                  : 'bg-destructive/10 text-destructive'
                  }`}>
                  {lastScan.success ? (
                    <CheckCircle2 className="h-16 w-16 mx-auto mb-3" />
                  ) : (
                    <XCircle className="h-16 w-16 mx-auto mb-3" />
                  )}
                  <p className="text-2xl font-bold">{lastScan.message}</p>
                  {lastScan.clientName && (
                    <p className="text-lg mt-2 opacity-80">{lastScan.clientName}</p>
                  )}
                  {lastScan.sessionsRemaining !== undefined && lastScan.sessionsRemaining !== null && (
                    <div className="mt-4">
                      <Badge variant="outline" className={`text-lg py-1 px-4 border-2 ${
                        lastScan.sessionsRemaining === 0 ? 'border-destructive bg-destructive/20' : 'border-current'
                      }`}>
                        {lastScan.sessionsRemaining === 0 
                          ? '⚠️ Última sesión usada' 
                          : `${lastScan.sessionsRemaining} sesiones restantes`}
                      </Badge>
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2 text-sm text-muted-foreground justify-center">
                  <Clock className="h-4 w-4" />
                  {new Date(lastScan.timestamp).toLocaleTimeString('es-ES', {
                    hour: '2-digit',
                    minute: '2-digit',
                    second: '2-digit',
                  })}
                </div>
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <QrCode className="h-12 w-12 mx-auto mb-3 opacity-50" />
                <p>Esperando escaneo...</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recent Scans List */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="h-5 w-5 text-primary" />
              Escaneos Recientes
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3 max-h-[300px] overflow-y-auto pr-1">
              {recentScans.length > 0 ? recentScans.map((scan) => (
                <Link
                  key={scan.checkin_id}
                  to={`/memberships?search=${encodeURIComponent(scan.client_name || '')}&clientId=${encodeURIComponent(scan.client_id)}`}
                  className="flex items-center justify-between p-3 rounded-lg bg-muted/50 hover:bg-muted transition-all cursor-pointer group active:scale-[0.98] focus:ring-2 focus:ring-primary/50 block w-full"
                  title="Ver membresía del socio"
                >
                  <div className="flex items-center gap-3">
                    <div className={`h-10 w-10 rounded-full flex items-center justify-center transition-transform group-hover:scale-110 ${scan.result === 'allowed'
                      ? 'bg-success/10 text-success'
                      : 'bg-destructive/10 text-destructive'
                      }`}>
                      {scan.result === 'allowed' ? (
                        <CheckCircle2 className="h-5 w-5" />
                      ) : (
                        <XCircle className="h-5 w-5" />
                      )}
                    </div>
                    <div>
                      <p className="font-bold text-sm group-hover:text-primary transition-colors">
                        {scan.client_name || 'Desconocido'}
                        <ExternalLink className="h-3 w-3 inline-block ml-1 opacity-0 group-hover:opacity-100 transition-opacity" />
                      </p>
                      <p className="text-[10px] text-muted-foreground uppercase font-medium">
                        {new Date(scan.timestamp).toLocaleTimeString('es-ES', {
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </p>
                    </div>
                  </div>
                  <Badge variant={scan.result === 'allowed' ? 'success' : 'destructive'} className="font-bold">
                    {scan.result === 'allowed' ? 'OK' : 'DENY'}
                  </Badge>
                </Link>
              )) : (
                <p className="text-center text-muted-foreground py-4 text-sm">
                  No hay registros recientes hoy
                </p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      <AlertDialog
        open={!!pendingClient}
        onOpenChange={(open) => {
          if (!open && !isRegistering) setPendingClient(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              ¿Deseas hacer check-in manual a {pendingClient?.name}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              Se registrará la entrada del socio como asistencia por staff.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isRegistering}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              disabled={isRegistering}
              onClick={(e) => {
                e.preventDefault();
                handleManualCheckIn();
              }}
            >
              {isRegistering ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Registrando...
                </>
              ) : (
                'Confirmar check-in'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DashboardLayout>
  );
}
